// Web Speech API-implementering af DictationAudio.
//
// Bemærk:
//   - iOS Safari kræver at første utterance afgår fra en bruger-gesture
//     (klik/tast). Vi håndterer det ved at lade kald-stedet (DictationRound)
//     starte med en "Start oplæsning"-knap; subsekvente speak()-kald virker
//     derefter.
//   - Voice list er asynkron i Chrome/Firefox. `waitForVoices()` venter
//     på `voiceschanged`-event eller en kort timeout.

import type {
  DictationAudio,
  DictationAudioLocale,
  DictationSpeakOptions,
  DictationVoice,
} from './dictationAudio';

function pickVoicesForLocale(
  all: SpeechSynthesisVoice[],
  locale: DictationAudioLocale,
): SpeechSynthesisVoice[] {
  if (locale === 'en') {
    return all.filter((v) => /^en([-_]|$)/i.test(v.lang));
  }
  return all.filter((v) => /^da([-_]|$)/i.test(v.lang));
}

function pickDanishVoices(all: SpeechSynthesisVoice[]): SpeechSynthesisVoice[] {
  return pickVoicesForLocale(all, 'da');
}

function toDictationVoice(v: SpeechSynthesisVoice): DictationVoice {
  return {
    id: v.voiceURI,
    name: v.name,
    lang: v.lang,
    localService: v.localService,
  };
}

export class WebSpeechAudio implements DictationAudio {
  private readonly synth: SpeechSynthesis | null;
  private voicesReady: Promise<void>;
  private voicesResolved = false;

  constructor() {
    this.synth =
      typeof window !== 'undefined' && 'speechSynthesis' in window
        ? window.speechSynthesis
        : null;
    this.voicesReady = this.initVoices();
  }

  private initVoices(): Promise<void> {
    if (!this.synth) {
      this.voicesResolved = true;
      return Promise.resolve();
    }
    if (this.synth.getVoices().length > 0) {
      this.voicesResolved = true;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const finish = () => {
        if (this.voicesResolved) return;
        this.voicesResolved = true;
        resolve();
      };
      const onChange = () => finish();
      this.synth!.addEventListener('voiceschanged', onChange);
      // Fallback: many browsers fire voiceschanged late or not at all.
      window.setTimeout(finish, 1500);
    });
  }

  isAvailable(): boolean {
    if (!this.synth) return false;
    if (!this.voicesResolved) return false;
    const voices = this.synth.getVoices();
    return (
      pickDanishVoices(voices).length > 0 ||
      pickVoicesForLocale(voices, 'en').length > 0
    );
  }

  listDanishVoices(): DictationVoice[] {
    if (!this.synth) return [];
    return pickDanishVoices(this.synth.getVoices()).map(toDictationVoice);
  }

  waitForVoices(): Promise<void> {
    return this.voicesReady;
  }

  speak(text: string, opts: DictationSpeakOptions = {}): Promise<void> {
    if (!this.synth) return Promise.resolve();
    const trimmed = text.trim();
    if (!trimmed) return Promise.resolve();

    const locale: DictationAudioLocale = opts.audioLocale ?? 'da';
    const utter = new SpeechSynthesisUtterance(trimmed);
    utter.lang = locale === 'en' ? 'en-US' : 'da-DK';
    utter.rate = clamp(opts.rate ?? 0.85, 0.5, 1.4);
    utter.pitch = 1;
    utter.volume = 1;

    const localeVoices = pickVoicesForLocale(this.synth.getVoices(), locale);
    const chosen =
      (opts.voiceId &&
        localeVoices.find((v) => v.voiceURI === opts.voiceId)) ||
      localeVoices[0] ||
      pickDanishVoices(this.synth.getVoices())[0];
    if (chosen) utter.voice = chosen;

    return new Promise<void>((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      utter.onend = done;
      utter.onerror = done;
      try {
        this.synth!.speak(utter);
      } catch {
        done();
      }
    });
  }

  cancel(): void {
    if (!this.synth) return;
    try {
      this.synth.cancel();
    } catch {
      /* ignore */
    }
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
